import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import Marquee from "react-fast-marquee";
import { Trophy, Loader2, Zap, Coins } from "lucide-react";
import confetti from "canvas-confetti";

export default function BigScreen() {
  const { slug, zoneId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    let dead = false;
    const url = zoneId ? `/public/events/${slug}/display/${zoneId}` : `/public/events/${slug}/display`;
    const poll = () => api.get(url).then((r) => { if (!dead) setData(r.data); }).catch(() => {});
    poll();
    const iv = setInterval(poll, zoneId ? 1500 : 3000);
    return () => { dead = true; clearInterval(iv); };
  }, [slug, zoneId]);

  if (!data) return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="w-10 h-10 animate-spin text-primary" /></div>;

  const accent = data.event.theme || "#E63329";
  if (!zoneId) return <EventDisplay data={data} accent={accent} />;
  return <ZoneDisplay data={data} accent={accent} />;
}

function EventDisplay({ data, accent }) {
  const ev = data.event;
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col overflow-hidden">
      <div className="absolute inset-0 opacity-60 pointer-events-none" style={{ background: `radial-gradient(circle at 25% 15%, ${accent}14, transparent 45%), radial-gradient(circle at 78% 85%, rgba(0,90,200,0.06), transparent 45%)` }} />
      <div className="relative flex-1 flex flex-col items-center justify-center p-10 text-center">
        <div className="text-xl uppercase tracking-[0.35em] text-muted-foreground mb-3 animate-pop">{ev.event_type} · Live</div>
        <h1 className="font-head font-extrabold tracking-tight" style={{ fontSize: "6.5vw", lineHeight: 1, color: accent }}>{ev.name}</h1>
        <p className="mt-5 text-muted-foreground" style={{ fontSize: "1.6vw" }}>{data.player_count} players in the arena · Scan the QR to join</p>

        <div className="mt-12 w-full max-w-4xl">
          <h2 className="font-head font-extrabold mb-6 flex items-center justify-center gap-3" style={{ fontSize: "2.6vw" }}><Trophy className="text-gold" style={{ width: "2.6vw", height: "2.6vw" }} />{ev.terminology?.leaderboard || "Hall of Fame"}</h2>
          <div className="space-y-3">
            {data.leaderboard.slice(0, 5).map((r, i) => (
              <div key={r.player_id || r.name} className="flex items-center gap-5 brut rounded-2xl px-6 py-4 animate-pop" style={{ animationDelay: `${i * 80}ms`, borderColor: i === 0 ? accent : undefined }}>
                <div className="font-mono-num font-black grid place-items-center rounded-full" style={{ width: "3.2vw", height: "3.2vw", fontSize: "1.6vw", background: i < 3 ? "#EAB308" : "hsl(var(--secondary))", color: i < 3 ? "#000" : "hsl(var(--foreground))" }}>{r.rank}</div>
                <div className="flex-1 text-left font-head font-bold" style={{ fontSize: "1.9vw" }}>{r.name}</div>
                <div className="flex items-center gap-2 font-mono-num font-black text-cash" style={{ fontSize: "2.1vw" }}><Coins style={{ width: "1.8vw", height: "1.8vw" }} />{r.xp}</div>
              </div>
            ))}
            {data.leaderboard.length === 0 && <p className="text-muted-foreground text-2xl py-10">Waiting for the first scores…</p>}
          </div>
        </div>
      </div>
      <div className="relative border-t border-border bg-card py-4">
        <Marquee gradient={false} speed={50}>
          {(data.activity.length ? data.activity : [{ player_name: "Get ready", zone_name: "", xp: 0 }]).map((a, i) => (
            <span key={i} className="mx-10 font-mono-num text-muted-foreground flex items-center gap-1" style={{ fontSize: "1.4vw" }}>🎉 {a.player_name} {a.zone_name && `· ${a.zone_name}`} {a.xp > 0 && <span className="text-cash flex items-center gap-1"><Coins style={{ width: "1.2vw", height: "1.2vw" }} />+{a.xp}</span>}</span>
          ))}
        </Marquee>
      </div>
    </div>
  );
}

function ZoneDisplay({ data, accent }) {
  const live = data.live || {};
  const screen = live.screen || "welcome";
  const q = data.current_question;
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (screen === "countdown" && live.timer_ends_at) {
      const iv = setInterval(() => setCount(Math.max(0, Math.round((new Date(live.timer_ends_at) - new Date()) / 1000))), 250);
      return () => clearInterval(iv);
    }
  }, [screen, live.timer_ends_at]);

  useEffect(() => { if (screen === "winner") confetti({ particleCount: 220, spread: 120, origin: { y: 0.4 } }); }, [screen]);

  const wrap = (children) => (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center p-10 text-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-60 pointer-events-none" style={{ background: `radial-gradient(circle at 50% 30%, ${accent}14, transparent 55%)` }} />
      <div className="relative w-full max-w-5xl">{children}</div>
    </div>
  );

  if (screen === "welcome") return wrap(<>
    <Zap className="mx-auto mb-6 text-gold animate-floaty" style={{ width: "7vw", height: "7vw" }} />
    <h1 className="font-head font-extrabold" style={{ fontSize: "5.5vw", color: accent }}>{data.zone.name}</h1>
    <p className="mt-4 text-muted-foreground" style={{ fontSize: "1.8vw" }}>{data.zone.description}</p>
    <p className="mt-8 text-muted-foreground/70" style={{ fontSize: "1.4vw" }}>Waiting for the Game Master to start…</p>
  </>);

  if (screen === "spin") return wrap(<>
    <p className="text-muted-foreground uppercase tracking-widest" style={{ fontSize: "1.8vw" }}>Fortune Spin</p>
    <div className="my-10 mx-auto rounded-full border-8 grid place-items-center animate-glow" style={{ width: "24vw", height: "24vw", borderColor: accent }}>
      <div className="font-head font-extrabold" style={{ fontSize: "3.6vw", color: accent }}>{live.spin_result || "SPIN!"}</div>
    </div>
    <p className="text-muted-foreground" style={{ fontSize: "1.8vw" }}>Category: <span className="text-cash font-bold">{live.spin_result || "—"}</span></p>
  </>);

  if (screen === "countdown") return wrap(<>
    <p className="text-muted-foreground" style={{ fontSize: "2.2vw" }}>Get ready…</p>
    <div className="font-mono-num font-black animate-pop" style={{ fontSize: "20vw", color: accent }}>{count}</div>
  </>);

  if (screen === "winner") return wrap(<>
    <Trophy className="mx-auto text-gold animate-floaty" style={{ width: "9vw", height: "9vw" }} />
    <h1 className="font-head font-extrabold mt-4" style={{ fontSize: "6.5vw", color: "#EAB308" }}>WINNER!</h1>
    <p className="mt-4 font-head font-bold" style={{ fontSize: "2.8vw" }}>{live.winner || "Champion"}</p>
  </>);

  if ((screen === "question" || screen === "answer") && q) return wrap(<>
    <p className="text-muted-foreground font-mono-num" style={{ fontSize: "1.4vw" }}>Question {q.index + 1} / {q.total} · {q.category}</p>
    <h1 className="font-head font-extrabold my-8" style={{ fontSize: "4vw", lineHeight: 1.05 }}>{q.text}</h1>
    <div className="grid grid-cols-2 gap-5">
      {q.options.map((o, i) => {
        const correct = screen === "answer" && i === q.correct;
        return (
          <div key={i} className="rounded-2xl border px-6 py-6 font-head font-bold text-left transition-colors brut" style={{ fontSize: "2vw", background: correct ? "hsl(var(--cash))" : "hsl(var(--card))", borderColor: correct ? "hsl(var(--cash))" : undefined, color: correct ? "#fff" : "hsl(var(--foreground))" }}>
            <span className="font-mono-num opacity-50 mr-3">{String.fromCharCode(65 + i)}</span>{o}
          </div>
        );
      })}
    </div>
    {screen === "answer" && <p className="mt-8 text-cash font-head font-extrabold" style={{ fontSize: "2.2vw" }}>✓ Correct Answer</p>}
  </>);

  return wrap(<h1 className="font-head font-extrabold" style={{ fontSize: "4.5vw", color: accent }}>{data.zone.name}</h1>);
}
